// ============================================================================
// useProfile — Role-aware profile data fetcher
//
// Branches by user.role to call the correct role-specific endpoints:
//   Seafarer: /seafarers/profile/  (getMySeafarerProfile)
//   Shore:    /shore/profile/      (getMyShoreProfile)
//   Employer: /employers/company/my-profile/  (getMyEmployerProfile)
//
// Sub-data queries are ONLY enabled for the relevant role:
//   Seafarer: sea-service, certifications, documents
//   Shore:    work-experience, documents
//   Employer: none (company profile is self-contained)
// ============================================================================

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { QUERY_KEYS } from '@/lib/types/api';

// ── Seafarer API ──
import {
  getMySeafarerProfile,
  updateMySeafarerProfile,
  getSeaServiceRecords,
  getSeafarerCertifications,
  getSeafarerDocuments,
  createSeafarerCertification,
  updateSeafarerCertification,
  deleteSeafarerCertification,
  createSeaServiceRecord,
  updateSeaServiceRecord,
  deleteSeaServiceRecord,
  createSeafarerDocument,
  deleteSeafarerDocument,
} from '@/lib/api/seafarer';

// ── Shore API ──
import {
  getMyShoreProfile,
  updateMyShoreProfile,
  getShoreDocuments,
  getShoreWorkExperience,
} from '@/lib/api/shore';

// ── Employer API ──
import {
  getMyEmployerProfile,
  updateMyEmployerProfile,
} from '@/lib/api/employer';

// ── Types ──
import type { SeafarerProfile, CompanyProfile } from '@/lib/types/models';
import type { ShoreProfile, WorkExperience, ShoreDocument } from '@/lib/api/shore';
import type {
  User,
  Certification,
  SeaServiceRecord,
  Document,
} from '@/lib/types';

export type ProfileUnion = SeafarerProfile | ShoreProfile | CompanyProfile;

// ── Type guards ──
export function isSeafarerProfile(p: ProfileUnion | null): p is SeafarerProfile {
  return p !== null && 'currentRank' in p;
}
export function isShoreProfile(p: ProfileUnion | null): p is ShoreProfile {
  return p !== null && 'currentPosition' in p && typeof p.currentPosition !== 'string';
}
export function isEmployerProfile(p: ProfileUnion | null): p is CompanyProfile {
  return p !== null && 'companyName' in p && 'approvalStatus' in p;
}

// ── Return type ──
export interface UseProfileReturn {
  profile: ProfileUnion | null;
  user: User | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetchProfile: () => void;
  updateProfile: ReturnType<typeof useMutation<ProfileUnion, Error, Record<string, unknown>>>;
  // Sub-data (role-specific)
  documents: Document[] | ShoreDocument[] | undefined;
  isDocumentsLoading: boolean;
  seaService: SeaServiceRecord[] | WorkExperience[] | undefined;
  isSeaServiceLoading: boolean;
  certifications: Certification[] | [] | undefined;
  isCertificationsLoading: boolean;
  // ── Status ──
  isProfileMutating: boolean;
  isUpdating: boolean;
  // ── CRUD aliases (seafarer-specific, for compat) ──
  createCertification: typeof createSeafarerCertification;
  updateCertification: typeof updateSeafarerCertification;
  deleteCertification: typeof deleteSeafarerCertification;
  createSeaService: typeof createSeaServiceRecord;
  updateSeaService: typeof updateSeaServiceRecord;
  deleteSeaService: typeof deleteSeaServiceRecord;
  uploadDocument: typeof createSeafarerDocument;
  deleteDocument: typeof deleteSeafarerDocument;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useProfile(options?: { staleTime?: number; gcTime?: number }): UseProfileReturn {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const userId = user?.id;
  const role = user?.role;
  const isSeafarer = role === 'seafarer';
  const isShore = role === 'shore';
  const isEmployer = role === 'employer';

  // ─── Profile Query (role-aware) ──────────────────────────────────────────

  const profileQueryFn = useCallback(async (): Promise<ProfileUnion> => {
    if (isSeafarer) return (await getMySeafarerProfile()) as unknown as ProfileUnion;
    if (isShore) return (await getMyShoreProfile()) as unknown as ProfileUnion;
    if (isEmployer) return (await getMyEmployerProfile()) as unknown as ProfileUnion;
    throw new Error(`Unknown role: ${role}`);
  }, [isSeafarer, isShore, isEmployer, role]);

  const profileQuery = useQuery<ProfileUnion>({
    queryKey: userId ? QUERY_KEYS.PROFILE.DETAIL(userId) : ['profile', 'no-user'],
    queryFn: profileQueryFn,
    enabled: !!userId,
    staleTime: options?.staleTime ?? 1000 * 60 * 5,
    gcTime: options?.gcTime ?? 1000 * 60 * 30,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  // ─── Documents Query (seafarer + shore only) ─────────────────────────────

  const documentsQuery = useQuery({
    queryKey: userId ? QUERY_KEYS.PROFILE.DOCUMENTS(userId) : ['documents', 'no-user'],
    queryFn: async () => {
      if (isSeafarer) {
        const result = await getSeafarerDocuments();
        return result.results as unknown as Document[];
      }
      if (isShore) {
        const result = await getShoreDocuments();
        return result.results as unknown as ShoreDocument[];
      }
      return [];
    },
    enabled: !!userId && (isSeafarer || isShore),
    staleTime: options?.staleTime ?? 1000 * 60 * 2,
    gcTime: options?.gcTime ?? 1000 * 60 * 10,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // ─── Experience Query (seafarer + shore only) ────────────────────────────

  const experienceQuery = useQuery({
    queryKey: userId
      ? isShore ? ['work-experience', userId] : QUERY_KEYS.PROFILE.SEA_SERVICE(userId)
      : ['experience', 'no-user'],
    queryFn: async () => {
      if (isSeafarer) {
        const result = await getSeaServiceRecords();
        return result.results as unknown as SeaServiceRecord[];
      }
      if (isShore) {
        const result = await getShoreWorkExperience();
        return result.results as unknown as WorkExperience[];
      }
      return [];
    },
    enabled: !!userId && (isSeafarer || isShore),
    staleTime: options?.staleTime ?? 1000 * 60 * 2,
    gcTime: options?.gcTime ?? 1000 * 60 * 10,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // ─── Certifications Query (seafarer only) ────────────────────────────────

  const certificationsQuery = useQuery({
    queryKey: userId ? QUERY_KEYS.PROFILE.CERTIFICATIONS(userId) : ['certifications', 'no-user'],
    queryFn: async () => {
      if (isSeafarer) {
        const result = await getSeafarerCertifications();
        return result.results as unknown as Certification[];
      }
      return [] as Certification[];
    },
    enabled: !!userId && isSeafarer,
    staleTime: options?.staleTime ?? 1000 * 60 * 2,
    gcTime: options?.gcTime ?? 1000 * 60 * 10,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // ─── Profile Update Mutation (role-aware) ────────────────────────────────

  const updateMutation = useMutation<ProfileUnion, Error, Record<string, unknown>>({
    mutationFn: async (data) => {
      if (isSeafarer) {
        return (await updateMySeafarerProfile(
          data as unknown as Parameters<typeof updateMySeafarerProfile>[0]
        )) as unknown as ProfileUnion;
      }
      if (isShore) {
        return (await updateMyShoreProfile(
          data as unknown as Parameters<typeof updateMyShoreProfile>[0]
        )) as unknown as ProfileUnion;
      }
      if (isEmployer) {
        return (await updateMyEmployerProfile(
          data as unknown as Parameters<typeof updateMyEmployerProfile>[0]
        )) as unknown as ProfileUnion;
      }
      throw new Error(`Unknown role: ${role}`);
    },
    onSuccess: (data) => {
      if (userId) {
        queryClient.setQueryData(QUERY_KEYS.PROFILE.DETAIL(userId), data);
      }
    },
  });

  // ─── Derived State ───────────────────────────────────────────────────────

  const profileData = profileQuery.data ?? null;

  // Only count queries that are enabled for this role
  const isLoading =
    profileQuery.isLoading ||
    ((isSeafarer || isShore) && documentsQuery.isLoading) ||
    ((isSeafarer || isShore) && experienceQuery.isLoading) ||
    (isSeafarer && certificationsQuery.isLoading);

  const isError =
    profileQuery.isError ||
    ((isSeafarer || isShore) && documentsQuery.isError) ||
    ((isSeafarer || isShore) && experienceQuery.isError) ||
    (isSeafarer && certificationsQuery.isError);

  const error =
    (profileQuery.error as Error | null) ??
    (((isSeafarer || isShore) && documentsQuery.error) ? documentsQuery.error as Error : null) ??
    (((isSeafarer || isShore) && experienceQuery.error) ? experienceQuery.error as Error : null) ??
    ((isSeafarer && certificationsQuery.error) ? certificationsQuery.error as Error : null);

  return {
    profile: profileData,
    user,
    isLoading,
    isError,
    error,
    refetchProfile: () => {
      profileQuery.refetch();
      if (isSeafarer || isShore) {
        documentsQuery.refetch();
        experienceQuery.refetch();
      }
      if (isSeafarer) certificationsQuery.refetch();
    },
    updateProfile: updateMutation,
    documents: (isSeafarer || isShore) ? documentsQuery.data : [],
    isDocumentsLoading: (isSeafarer || isShore) ? documentsQuery.isLoading : false,
    seaService: (isSeafarer || isShore) ? experienceQuery.data : [],
    isSeaServiceLoading: (isSeafarer || isShore) ? experienceQuery.isLoading : false,
    certifications: isSeafarer ? certificationsQuery.data : [],
    isCertificationsLoading: isSeafarer ? certificationsQuery.isLoading : false,
    isProfileMutating: updateMutation.isPending,
    isUpdating: updateMutation.isPending,
    // CRUD aliases (seafarer-specific, for compatibility)
    createCertification: createSeafarerCertification,
    updateCertification: updateSeafarerCertification,
    deleteCertification: deleteSeafarerCertification,
    createSeaService: createSeaServiceRecord,
    updateSeaService: updateSeaServiceRecord,
    deleteSeaService: deleteSeaServiceRecord,
    uploadDocument: createSeafarerDocument,
    deleteDocument: deleteSeafarerDocument,
  };
}